import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type WorkspaceHealthResult } from '@whinerp/client';
import { Icon, InlineAction, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * Setup > Health (F-148).
 *
 * One row per signal, each carrying the number it was judged on. A dashboard
 * of coloured dots makes somebody click through to find out what happened; the
 * number and the sentence are on the row, so the click is optional.
 *
 * 'Not measured' is drawn differently from 'working', because they are
 * different facts and a dashboard is believed about which is which.
 */

const TONE = {
  ok: { icon: 'CheckCircle', className: 'text-success-text' },
  warn: { icon: 'Warning', className: 'text-warning-text' },
  down: { icon: 'WarningCircle', className: 'text-danger' },
  'not-measured': { icon: 'Question', className: 'text-ink-meta' },
} as const;

export function HealthPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const client = useQueryClient();
  const navigate = useNavigate();

  const health = useQuery<WorkspaceHealthResult, ApiError>({
    queryKey: ['workspace-health'],
    queryFn: () => api.workspaceHealth({}),
  });

  /*
   * Reading the dashboard also rings the bell.
   *
   * There is no scheduler in this product yet, and a notification that waits
   * for infrastructure nobody has built is a notification that never arrives.
   * The alert is idempotent by state, so doing it here rings once and not on
   * every refresh.
   */
  const alert = useMutation({
    mutationFn: () => api.raiseHealthAlerts({ body: {} }),
    onSuccess: (result) => {
      if (result.announced.length > 0) {
        void client.invalidateQueries({ queryKey: ['notifications'] });
      }
    },
  });
  const failing = health.data?.signals.some((signal) => signal.status === 'down') ?? false;
  const raise = alert.mutate;
  useEffect(() => {
    if (failing) raise();
  }, [failing, raise]);

  // A skeleton means "still loading". Rendering one on error is a spinner that
  // never resolves.
  if (health.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (health.isError) return <></>;

  return (
    <Panel data-region="health" id="health">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('health.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">
          {t('health.body', {
            values: { hours: formatCount(health.data.windowHours, format) },
          })}
        </p>

        <ul className="flex flex-col gap-2" data-health-signals>
          {health.data.signals.map((signal) => {
            const tone = TONE[signal.status];
            return (
              <li
                key={signal.key}
                data-health-signal={signal.key}
                data-health-status={signal.status}
                className="flex flex-wrap items-baseline gap-2 rounded-chip bg-sunken px-3 py-2"
              >
                <span className={tone.className}>
                  <Icon name={tone.icon} size="inline" decorative />
                </span>
                <span className="text-body text-ink">{signal.label}</span>
                {/* The number it was judged on, not only the colour. A dot
                    alone makes somebody click to find out what happened. */}
                {signal.value === null ? (
                  <span data-health-unmeasured className="text-meta text-ink-meta">
                    {t('health.notMeasured')}
                  </span>
                ) : (
                  <span className="text-meta text-ink-meta">
                    <Num data-health-value>{formatCount(signal.value, format)}</Num>{' '}
                    {signal.unit}
                  </span>
                )}
                <span className="ms-auto text-meta text-ink-meta">
                  {t(`health.status.${signal.status}` as 'health.status.ok')}
                </span>
                <p data-health-detail className="w-full text-meta text-ink-meta">
                  {signal.detail}
                </p>
                {signal.href ? (
                  <InlineAction
                    data-health-look={signal.key}
                    onClick={() => navigate(signal.href!)}
                  >
                    {t('health.look')}
                  </InlineAction>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Panel>
  );
}
